import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { markWorkorderDone } from "@/lib/api";
import { fmtDate } from "@/components/common";

// One click on a recurring Facilities task: sets Last Completed = today,
// which pushes Next Due forward, and resets Status to "Not Started" for the next cycle.
// Also keeps a running log of completion dates (maintenance paper-trail).
export default function MarkDoneButton({ row, path = "workorders" }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const history = Array.isArray(row.history) ? [...row.history].reverse() : [];

  const markDone = async () => {
    setSaving(true);
    try {
      await markWorkorderDone(row.id);
      qc.invalidateQueries({ queryKey: [path] });
      qc.invalidateQueries({ queryKey: ["reminders"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success("Marked done — next reminder scheduled");
    } catch {
      toast.error("Couldn't mark done");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <Button
        data-testid="mark-done-btn"
        onClick={markDone}
        disabled={saving}
        variant="outline"
        className="h-7 rounded-none border-2 border-gray-900 px-2 text-xs font-bold uppercase tracking-wide hover:bg-gray-900 hover:text-white"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Done</>}
      </Button>
      {history.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="history-trigger"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-gray-500 hover:text-gray-900"
            >
              <History className="w-3.5 h-3.5" /> {history.length}× done
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-56">
            <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-2">Completion Log</div>
            <ul data-testid="history-list" className="space-y-1 max-h-52 overflow-auto">
              {history.map((d, i) => (
                <li key={i} className="text-sm text-gray-700 border-b border-gray-100 pb-1 last:border-0">{fmtDate(d)}</li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
